import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, CheckSquare, X } from 'lucide-react'
import { useState } from 'react'
import { api, TaskItem } from '../api/client'
import { EmptyState } from '../components/ui/Empty'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'

function formatDue(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

type Tab = 'pending' | 'done'

export function Tasks() {
  const qc = useQueryClient()
  const { haptic, confirm, close } = useTelegram()
  const [tab, setTab] = useState<Tab>('pending')
  const [activeTag, setActiveTag] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery<TaskItem[]>({
    queryKey: ['tasks', tab],
    queryFn: () => api.get(`/api/tasks?status=${tab}`),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  })

  const completeMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/api/tasks/${id}`, { status: 'done' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['today'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['today'] })
    },
  })

  const handleComplete = (task: TaskItem) => {
    haptic.impactOccurred('light')
    completeMutation.mutate(task.id)
  }

  const handleDelete = (task: TaskItem) => {
    haptic.impactOccurred('light')
    confirm('Удалить задачу?', ok => {
      if (ok) deleteMutation.mutate(task.id)
    })
  }

  const handleTagClick = (tag: string) => {
    haptic.impactOccurred('light')
    setActiveTag(prev => prev === tag ? null : tag)
  }

  // Client-side tag filter
  const displayed = activeTag
    ? (data ?? []).filter(t => (t.tags ?? []).includes(activeTag))
    : (data ?? [])

  const isEmpty = !isLoading && !error && (data ?? []).length === 0

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Задачи</p>

        {/* Segmented control */}
        <div className="segmented-control mt-3">
          {(['pending', 'done'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setActiveTag(null) }}
              className={`segment btn-active ${tab === t ? 'active' : ''}`}
            >
              {t === 'pending' ? 'Активные' : 'Выполненные'}
            </button>
          ))}
        </div>

        {/* Active tag filter chip */}
        {activeTag && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-ink-2">Тег:</span>
            <button
              onClick={() => setActiveTag(null)}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent-dim border border-accent text-xs text-accent font-medium"
            >
              #{activeTag}
              <X size={11} />
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 px-4 pb-6 space-y-2">
        {isLoading && Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)}

        {error && <ErrorCard onRetry={refetch} />}

        {isEmpty && tab === 'pending' && (
          <EmptyState
            icon={<CheckSquare size={48} />}
            title="Активных задач нет"
            description={'Попроси Delo создать задачу:\n\n«создай задачу купить билеты до пятницы»\n«запиши: подготовить презентацию, высокий приоритет»'}
            cta="Открыть чат с Delo"
            onCta={close}
          />
        )}

        {isEmpty && tab === 'done' && (
          <EmptyState
            icon={<CheckCircle size={48} />}
            title="Пока ничего не выполнено"
            description="Отмеченные задачи появятся здесь"
          />
        )}

        {!isEmpty && activeTag && displayed.length === 0 && (
          <p className="text-sm text-ink-2 text-center py-8">
            Задач с тегом #{activeTag} нет
          </p>
        )}

        {displayed.map(task => {
          const due = formatDue(task.due_at)
          const isDone = task.status === 'done'
          const tags = task.tags ?? []

          return (
            <div
              key={task.id}
              className={`bg-surface-1 border border-line-1 rounded-xl p-4 flex items-start gap-3 transition-all ${
                isDone ? 'opacity-40' : ''
              }`}
            >
              {/* Priority dot */}
              <span className={`mt-2 w-2 h-2 rounded-full shrink-0 ${
                task.priority === 'high' ? 'bg-danger' :
                task.priority === 'low'  ? 'bg-ink-2' : 'bg-warning'
              }`} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-base text-ink leading-snug ${isDone ? 'line-through' : ''}`}>
                  {task.title}
                </p>
                {task.description && (
                  <p className="text-sm text-ink-1 mt-0.5">{task.description}</p>
                )}
                {due && (
                  <p className="text-xs text-ink-2 mt-1 tabular-nums">{due}</p>
                )}
                {/* Tag pills */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {tags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => !isDone && handleTagClick(tag)}
                        className={`text-[11px] px-1.5 py-0.5 rounded-md border transition-colors ${
                          activeTag === tag
                            ? 'bg-accent-dim border-accent text-accent'
                            : 'bg-surface-2 border-line-1 text-ink-2'
                        } ${isDone ? 'pointer-events-none' : 'btn-active'}`}
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              {tab === 'pending' && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleComplete(task)}
                    disabled={completeMutation.isPending}
                    className="btn-active w-6 h-6 rounded-full border-2 border-accent flex items-center justify-center disabled:opacity-40 hover:bg-accent-dim transition-colors"
                    aria-label="Выполнено"
                  />
                  <button
                    onClick={() => handleDelete(task)}
                    disabled={deleteMutation.isPending}
                    className="btn-active text-ink-2 p-1 rounded hover:text-danger disabled:opacity-40 transition-colors"
                    aria-label="Удалить"
                  >
                    <X size={15} />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
